import React, { useEffect, useMemo, useRef, useState } from 'react'
import StorefrontIcon from '@mui/icons-material/Storefront'
import PaidIcon from '@mui/icons-material/Paid'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import SavingsIcon from '@mui/icons-material/Savings'
import { MotionCard, MotionIcon, MotionSection } from '../motion/Motion'

type StatItem = {
  icon: React.ReactNode
  value: number
  suffix?: string
  label: string
}

function formatK(value: number) {
  const k = value / 1000
  const isInt = Number.isInteger(k)
  return `${isInt ? k.toFixed(0) : k.toFixed(1)}k`
}

function StatCard({ item, inView, showDivider }: { item: StatItem; inView: boolean; showDivider: boolean }) {
  const count = useCountUp(item.value, inView)

  return (
    <MotionCard
      className={`flex flex-col items-center text-center px-2 py-2 ${showDivider ? 'lg:border-r lg:border-pink-500/30' : ''}`}
    >
      <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
        <MotionIcon>{item.icon}</MotionIcon>
      </div>

      <p className="mt-3 text-xl sm:text-2xl font-bold text-gray-900">
        {item.suffix ? formatK(count) : count}
      </p>

      <p className="mt-1 text-xs sm:text-sm text-gray-700 max-w-[170px]">{item.label}</p>
    </MotionCard>
  )
}

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || inView) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [inView])

  return { ref, inView }
}

function useCountUp(target: number, enabled: boolean, durationMs = 900) {
  const [value, setValue] = useState(0)

  useEffect(() => {
    if (!enabled) return

    let raf = 0
    const start = performance.now()

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / durationMs)
      const next = Math.round(target * progress)
      setValue(next)

      if (progress < 1) raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [target, enabled, durationMs])

  return value
}

export default function AboutStatsSection() {
  const stats: StatItem[] = useMemo(
    () => [
      {
        icon: <StorefrontIcon fontSize="small" className="text-pink-500" />,
        value: 10500,
        suffix: 'k',
        label: 'Sallers active our site',
      },
      {
        icon: <PaidIcon fontSize="small" className="text-pink-500" />,
        value: 33000,
        suffix: 'k',
        label: 'Monthly Product Sale',
      },
      {
        icon: <CardGiftcardIcon fontSize="small" className="text-pink-500" />,
        value: 45500,
        suffix: 'k',
        label: 'Customer active in our site',
      },
      {
        icon: <SavingsIcon fontSize="small" className="text-pink-500" />,
        value: 25000,
        suffix: 'k',
        label: 'Anual gross sale in our site',
      },
    ],
    []
  )

  const { ref, inView } = useInViewOnce<HTMLDivElement>()

  return (
    <MotionSection className="w-full py-10 lg:py-12">
      <div ref={ref} className="w-[96%] mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-8">
          {stats.map((item, idx) => (
            <StatCard key={item.label} item={item} inView={inView} showDivider={idx < 3} />
          ))}
        </div>
      </div>
    </MotionSection>
  )
}
